import { z } from 'zod';
import { AppListing, AppMedia } from './apps.js';
import { Email } from './auth.js';
import { Platform, Slug, Ulid, UserIdentity } from './common.js';
/**
 * Handing an app to a tester (plan §5, §7).
 *
 * Two routes, because they solve different problems:
 *
 *  - **A direct grant** names someone who already has an account. Precise, revocable, and
 *    the tester does not have to do anything.
 *  - **A redeem code** is for everyone else — someone who has not signed up yet, a room full
 *    of people at a playtest, a link in a chat. It carries its own channel and its own limits.
 *
 * Both feed the same `app_entitlements` view, so nothing downstream has to know which was used.
 */
export const GrantAccessRequest = z.object({
    /** Must already have a BoardFlight account; a redeem code covers everyone else. */
    email: Email,
    /** Channel slug. Omitted means "whatever the app's default channel is", and keeps meaning
     *  that if the default later changes. */
    channel: Slug.optional(),
});
export const AppAccessEntry = z.object({
    ...UserIdentity.shape,
    channelId: Ulid.nullable(),
    channelSlug: z.string().nullable(),
    /** A name for a team-mate, `@handle` for anybody else, null if the granter's account is gone. */
    grantedBy: z.string().nullable(),
    createdAt: z.number().int(),
});
/* --------------------------------------------------------------------------- redeem codes */
export const CreateRedeemCodeRequest = z.object({
    /** Shown in the dashboard so a code is recognisable, e.g. "Playtest, 12 March". */
    label: z.string().trim().max(120).optional(),
    channel: Slug.optional(),
    /** Null means unlimited. */
    maxUses: z.number().int().min(1).max(100_000).optional(),
    expiresInDays: z.number().int().min(1).max(3650).optional(),
});
export const RedeemCodeSummary = z.object({
    id: Ulid,
    appId: Ulid,
    /** Enough to recognise the code in a list; never enough to redeem it. */
    codePrefix: z.string(),
    label: z.string().nullable(),
    channelId: Ulid.nullable(),
    channelSlug: z.string().nullable(),
    maxUses: z.number().int().nullable(),
    uses: z.number().int(),
    expiresAt: z.number().int().nullable(),
    revokedAt: z.number().int().nullable(),
    createdAt: z.number().int(),
});
/** The code itself appears in this response and nowhere else, ever. */
export const CreateRedeemCodeResponse = z.object({
    code: RedeemCodeSummary,
    secret: z.string(),
});
export const RedeemRequest = z.object({
    /** Accepted in whatever shape it was typed — case and dashes are normalized away. */
    code: z.string().trim().min(1).max(64),
});
export const RedeemResponse = z.object({
    appId: Ulid,
    appName: z.string(),
    teamName: z.string(),
    channelSlug: z.string().nullable(),
    /** True when this account had already redeemed this code; redeeming twice is not an error. */
    alreadyRedeemed: z.boolean(),
});
/* ------------------------------------------------------------------------------- preview */
export const RedeemPreviewRequest = RedeemRequest;
/** The newest build the code's channel would hand over, as a stranger may see it. */
export const RedeemPreviewBuild = z.object({
    versionName: z.string(),
    versionCode: z.number().int(),
    size: z.number().int(),
    releaseNotes: z.string().nullable(),
    createdAt: z.number().int(),
});
/**
 * What a redeem code is *for*, answered without an account.
 *
 * Deliberately unauthenticated: asking someone to sign up before telling them what they are
 * signing up for is the reason redeem links go unclicked. It says nothing the redemption
 * itself would not immediately reveal, and holding the code is the whole credential — the
 * same one the codes have always been.
 */
export const RedeemPreview = z.object({
    appName: z.string(),
    description: z.string().nullable(),
    platform: Platform,
    packageName: z.string(),
    iconUrl: z.string().nullable(),
    /** The same store page the library and the app detail screen render (plan §8). */
    listing: AppListing,
    media: z.array(AppMedia),
    teamName: z.string(),
    /** The channel this code grants, resolved through the app's default when it names none. */
    channelName: z.string().nullable(),
    channelSlug: z.string().nullable(),
    /** What the developer called the code, e.g. "Playtest, 12 March". */
    label: z.string().nullable(),
    expiresAt: z.number().int().nullable(),
    /**
     * True when every use is spent. Reported *before* sign-up on purpose: the alternative is
     * someone creating an account and only then being told the code was full.
     */
    exhausted: z.boolean(),
    /** Null when the channel has no build a tester may see yet. */
    latestBuild: RedeemPreviewBuild.nullable(),
});
//# sourceMappingURL=distribution.js.map